import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import type { Trace } from '@/features/traceability/type/Tool';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getMetricAxisFormatter, shortToolId, truncateLabel } from '../../utils/format';
import type { ChartConfig } from '../../type/dynamicChart';
import { COLORS, getColorForCategory } from '../../utils/palette';
import { formatXValue, getMetricFormatter, getMetricUnit } from '@/utils/format';
import {
  aggregateTracesByTool,
  aggregateTracesByCategory,
  aggregateTracesByReference,
} from '../../utils/trace';
import { getCategoryValue, getMetricValue, getXMetricValue, isCategoricalXAxis } from '../../utils/helper';
import { computeYFromTraces } from '../../utils/aggregationUtils';
import { SmartLegend } from './SmartLegend';
import type { LegendItem } from './SmartLegend';
import { ChartEmptyState } from './ChartEmptyState';
import { useTranslation } from '@/hooks/useTranslation';
import { getSafeAggregationLevel } from '../../utils/chartSafety';
import { getMetricLabelKey } from '../../utils/chartDefaults';

type TLBarChartProps = {
  config: ChartConfig;
  traces: Trace[];
};

// Type for chart data items
type ChartDataItem = {
  x: string | number;
  y: number;
  toolId: string;
  fullToolId: string;
  traceId?: string;
  fullTraceId?: string;
  category: string;
  index: number;
  traceCount: number;
  toolCount?: number;
  reference?: string;
};

// Helper to get color for a category
// Check if X-axis is a categorical dimension
export function TLBarChart({ config, traces }: TLBarChartProps) {
  const { t } = useTranslation();

  // Check if X-axis is categorical dimension
  const isCategorical = isCategoricalXAxis(config.xAxis.metric);

  // For categorical X-axis, always aggregate by category
  // For toolId X-axis, respect aggregationLevel setting with safety check
  const aggregationLevel = getSafeAggregationLevel(
    isCategorical ? 'category' : config.aggregatedBy,
    traces,
    isCategorical
  );

  const aggregatedData = useMemo(() => {
    if (isCategorical) {
      // Aggregate traces by categorical dimension
      return aggregateTracesByCategory(
        traces,
        config.xAxis.metric as 'machine' | 'material' | 'toolHolder' | 'lubrication'
      );
    }

    if (aggregationLevel === 'reference') {
      return aggregateTracesByReference(traces);
    }

    if (aggregationLevel === 'trace') {
      return traces;
    }
    // Aggregate by tool
    return aggregateTracesByTool(traces);
  }, [traces, aggregationLevel, isCategorical, config.xAxis.metric]);

  // Transform data into chart data
  const chartData = useMemo(() => {
    const data = aggregatedData
      .map((item, index) => {
        // Handle CategoryAggregation differently
        if ('category' in item) {
          // CategoryAggregation from categorical X-axis — use computeYFromTraces with rawTraces
          const yValue = computeYFromTraces(item.rawTraces, config.yAxis.metric, config.yAxis.aggregation);

          return {
            x: item.category, // Use category name as X value
            y: yValue,
            toolId: item.category, // Display category name instead of toolId
            fullToolId: item.category,
            traceId: undefined,
            fullTraceId: undefined,
            category: item.category, // Color by category itself
            index,
            traceCount: item.traceCount,
          };
        }

        // Handle ReferenceAggregation — keep existing getMetricValue (not in F2 scope)
        if ('reference' in item && 'toolCount' in item) {
          const yValue = getMetricValue(item, config.yAxis.metric);
          if (yValue === null) return null;

          return {
            x: item.reference, // Use reference as X value
            y: yValue,
            toolId: item.reference, // Use reference as toolId for display
            fullToolId: item.reference,
            traceId: undefined,
            fullTraceId: undefined,
            category: item.reference, // Color by reference
            index,
            traceCount: item.traceCount,
            toolCount: item.toolCount,
          };
        }

        // Handle ToolAggregation (has rawTraces) vs raw Trace
        if ('rawTraces' in item) {
          // ToolAggregation — use computeYFromTraces with rawTraces
          const yValue = computeYFromTraces(item.rawTraces, config.yAxis.metric, config.yAxis.aggregation);
          const xValue =
            aggregationLevel === 'trace' ? index : getXMetricValue(item, config.xAxis.metric);
          if (xValue === null) return null;

          return {
            x: xValue,
            y: yValue,
            toolId: shortToolId(item.toolId),
            fullToolId: item.toolId,
            traceId: undefined,
            fullTraceId: undefined,
            category: getCategoryValue(item, config.options.colorBy),
            index,
            traceCount: item.traceCount,
          };
        }

        // Handle normal Trace (raw trace — single trace, no aggregation needed)
        const yValue = getMetricValue(item, config.yAxis.metric);

        if (yValue === null) return null;

        // For trace level: use index as x, for tool level: use metric value
        const xValue =
          aggregationLevel === 'trace' ? index : getXMetricValue(item, config.xAxis.metric);

        if (xValue === null) return null;

        return {
          x: xValue,
          y: yValue,
          toolId: shortToolId(item.toolId),
          fullToolId: item.toolId,
          traceId: 'traceId' in item ? item.traceId : undefined,
          fullTraceId: 'traceId' in item ? item.traceId : undefined,
          // For trace level, color by tool; otherwise use config
          category:
            aggregationLevel === 'trace'
              ? shortToolId(item.toolId)
              : getCategoryValue(item, config.options.colorBy),
          index,
          // Add trace count for aggregated data
          traceCount: 'traceCount' in item ? item.traceCount : 1,
        };
      })
      .filter((d): d is NonNullable<typeof d> => d !== null);

    // Sort data based on sortOrder option
    if (config.options.sortOrder === 'asc') {
      return data.sort((a, b) => (a.y as number) - (b.y as number));
    } else if (config.options.sortOrder === 'desc') {
      return data.sort((a, b) => (b.y as number) - (a.y as number));
    } else if (config.options.sortOrder === 'alpha') {
      return data.sort((a, b) => String(a.x).localeCompare(String(b.x)));
    }

    return data;
  }, [aggregatedData, config, aggregationLevel]);

  // Get unique categories for color mapping
  const categories = useMemo(
    () => Array.from(new Set(chartData.map((d) => d.category))),
    [chartData]
  );

  // Prepare legend items for SmartLegend
  const legendItems: LegendItem[] = useMemo(() => {
    return categories.map((cat) => ({
      value: cat,
      label: cat,
      color: getColorForCategory(cat, categories),
    }));
  }, [categories]);

  // Determine if we need stacked bars or grouped bars
  // For trace level, always show individual bars colored by tool
  // For categorical X-axis, don't use stacked bars
  const useStackedBars =
    !isCategorical &&
    aggregationLevel === 'tool' &&
    config.options.colorBy &&
    categories.length > 1;

  // For stacked/grouped bars, we need to reshape data
  const finalChartData = useMemo(() => {
    if (!useStackedBars) {
      return chartData;
    }

    // Group data by x value
    const groupedByX = chartData.reduce(
      (acc, item) => {
        const xKey = String(item.x);
        if (!acc[xKey]) {
          acc[xKey] = { x: item.x, toolId: item.toolId };
        }
        acc[xKey][item.category] = item.y;
        return acc;
      },
      {} as Record<string, Record<string, unknown>>
    );

    return Object.values(groupedByX);
  }, [chartData, useStackedBars]);

  // Formatters
  const xFormatter = isCategorical
    ? (value: number | string) => String(value) // Show category name as-is
    : aggregationLevel === 'trace'
      ? (value: number | string) => `Trace ${Number(value) + 1}`
      : formatXValue;

  const yFormatter = getMetricFormatter(config.yAxis.metric);

  const xAxisFormatter = isCategorical
    ? (value: number | string) => truncateLabel(String(value)) // Tooltip keeps the full name
    : aggregationLevel === 'trace'
      ? (value: number | string) =>
          t('analytics.dashboard.complexCharts.axis.traceNumber', { number: Number(value) + 1 })
      : formatXValue;

  const yAxisFormatter = getMetricAxisFormatter(config.yAxis.metric);
  const yUnit = getMetricUnit(config.yAxis.metric);

  if (chartData.length === 0) {
    return <ChartEmptyState variant="no-data" />;
  }

  return (
    <div className="space-y-2">
      <ResponsiveContainer width="100%" height={450}>
        <BarChart data={finalChartData} margin={{ top: 8, right: 16, bottom: 60, left: 52 }}>
          {config.options.showGridLines !== false && <CartesianGrid strokeDasharray="3 3" />}
          <XAxis
            dataKey="x"
            angle={-45}
            textAnchor="end"
            height={52}
            tickFormatter={xAxisFormatter}
            tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
            label={{
              value:
                aggregationLevel === 'trace'
                  ? t('analytics.dashboard.complexCharts.axis.traceIndex')
                  : t(getMetricLabelKey(config.xAxis.metric)),
              position: 'insideBottom',
              offset: -40,
              fill: 'var(--muted-foreground)',
            }}
          />
          <YAxis
            tickFormatter={yAxisFormatter}
            tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
            label={{
              value: `${t(getMetricLabelKey(config.yAxis.metric))} ${yUnit ? `(${yUnit})` : ''}`,
              angle: -90,
              position: 'insideLeft',
              style: { textAnchor: 'middle' },
              fill: 'var(--muted-foreground)',
            }}
          />
          <Tooltip
            content={({ payload, label }) => {
              if (!payload || payload.length === 0) return null;

              // Get the first payload item to access trace/tool/reference info
              const firstItem = payload[0].payload as ChartDataItem;

              // Reference aggregation tooltip
              if ('toolCount' in firstItem && 'reference' in firstItem) {
                return (
                  <div className="bg-popover border border-border shadow-md rounded-lg p-3 text-xs">
                    <p className="font-medium">
                      {t('analytics.dashboard.complexCharts.tooltip.reference')}:{' '}
                      {firstItem.reference}
                    </p>
                    <p className="font-mono">
                      {t('analytics.dashboard.complexCharts.tooltip.toolCount', {
                        count: firstItem.toolCount || 0,
                      })}
                    </p>
                    <p className="font-mono">
                      {t('analytics.dashboard.complexCharts.tooltip.aggregatedFromTools', {
                        count: firstItem.toolCount || 0,
                      })}
                    </p>
                    <p className="font-mono">
                      {t(getMetricLabelKey(config.yAxis.metric))}: {yFormatter(firstItem.y)}
                    </p>
                    <p className="font-mono">
                      {t('analytics.dashboard.complexCharts.tooltip.traceCount', {
                        count: firstItem.traceCount,
                      })}
                    </p>
                  </div>
                );
              }

              return (
                <div className="bg-popover border border-border shadow-md rounded-lg p-3 text-xs">
                  {isCategorical ? (
                    // Categorical X-axis: show category name as main identifier
                    <>
                      <p className="font-medium">
                        {t(getMetricLabelKey(config.xAxis.metric))}: {firstItem.toolId}
                      </p>
                      {payload.map((entry, index) => (
                        <p key={index} className="font-mono" style={{ color: entry.color }}>
                          {entry.name}: {yFormatter(entry.value as number)}
                        </p>
                      ))}
                      {firstItem.traceCount && firstItem.traceCount > 1 && (
                        <p className="text-muted-foreground">
                          {t('analytics.dashboard.complexCharts.tooltip.aggregatedFrom', {
                            count: firstItem.traceCount,
                          })}
                        </p>
                      )}
                    </>
                  ) : aggregationLevel === 'trace' ? (
                    <>
                      <p className="font-medium">
                        {t('analytics.dashboard.complexCharts.tooltip.tool')}:{' '}
                        <Link
                          to={`/traceability/tools/${encodeURIComponent(firstItem.fullToolId)}`}
                          className="text-primary hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {firstItem.fullToolId}
                        </Link>
                      </p>
                      {firstItem.traceId && (
                        <p className="font-mono">
                          {t('analytics.dashboard.complexCharts.tooltip.trace')}:{' '}
                          {firstItem.traceId}
                        </p>
                      )}
                      <p className="font-mono">
                        {t('analytics.dashboard.complexCharts.tooltip.traceIndex')}:{' '}
                        {typeof firstItem.x === 'number' ? firstItem.x + 1 : firstItem.x}
                      </p>
                    </>
                  ) : (
                    <p className="font-medium">
                      {t(getMetricLabelKey(config.xAxis.metric))}: {xFormatter(String(label ?? ''))}
                    </p>
                  )}
                  {!isCategorical &&
                    payload.map((entry, index) => (
                      <p key={index} className="font-mono" style={{ color: entry.color }}>
                        {entry.name}: {yFormatter(entry.value as number)}
                      </p>
                    ))}
                </div>
              );
            }}
          />

          {useStackedBars ? (
            // Render multiple bars for each category (stacked or grouped) - tool level only
            categories.map((category) => (
              <Bar
                key={category}
                dataKey={category}
                name={category}
                fill={getColorForCategory(category, categories)}
                stackId={config.options.colorBy ? 'stack' : undefined}
              />
            ))
          ) : aggregationLevel === 'trace' ? (
            // Trace level: single bar series with cells colored by tool
            <Bar
              dataKey="y"
              name={t(getMetricLabelKey(config.yAxis.metric))}
              label={
                config.options.showDataLabels
                  ? {
                      position: 'top',
                      formatter: (value: unknown) => yFormatter(value as number),
                    }
                  : undefined
              }
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={getColorForCategory(entry.category, categories)}
                />
              ))}
            </Bar>
          ) : (
            // Single bar series for tool level without stacking
            <Bar
              dataKey="y"
              name={t(getMetricLabelKey(config.yAxis.metric))}
              fill={COLORS[0]}
              label={
                config.options.showDataLabels
                  ? {
                      position: 'top',
                      formatter: (value: unknown) => yFormatter(value as number),
                    }
                  : undefined
              }
            />
          )}
        </BarChart>
      </ResponsiveContainer>

      {/* SmartLegend - Show only for trace level or when using stacked bars */}
      {(useStackedBars || aggregationLevel === 'trace') && (
        <SmartLegend payload={legendItems} layout="horizontal" maxVisibleItems={10} />
      )}
    </div>
  );
}
